/**
 * AdminOrdersPage - Liste des commandes
 * Affiche toutes les commandes avec filtres et recherche
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ShoppingCart,
  Search,
  Filter,
  Eye,
  RefreshCw,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
  processing: { label: 'En préparation', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package },
  shipped: { label: 'Expédiée', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  refunded: { label: 'Remboursée', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: AlertCircle }
};

export function AdminOrdersPage() {
  const navigate = useNavigate();
  const { isLight } = useAdminTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        
        // Calculer les stats
        const orderList = data.orders || [];
        setStats({
          total: orderList.length,
          pending: orderList.filter(o => o.status === 'pending').length,
          processing: orderList.filter(o => o.status === 'processing').length,
          shipped: orderList.filter(o => o.status === 'shipped').length,
          delivered: orderList.filter(o => o.status === 'delivered').length
        });
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filtrer les commandes
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (amount, currency = 'ILS') => {
    const symbols = { ILS: '₪', EUR: '€', USD: '$' };
    return `${symbols[currency] || '₪'}${(amount || 0).toFixed(2)}`;
  };

  const cardClass = isLight 
    ? 'bg-white border-gray-200' 
    : 'bg-zinc-900/80 border-zinc-800';
  const textClass = isLight ? 'text-gray-900' : 'text-white';
  const mutedClass = isLight ? 'text-gray-600' : 'text-gray-400';

  return (
    <AdminPageWrapper 
      title="Commandes" 
      subtitle="Gérez et suivez toutes vos commandes"
    >
      <div className="space-y-6" data-testid="admin-orders-page">
        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button onClick={fetchOrders} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={cardClass}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${textClass}`}>{stats.total}</p>
                <p className={`text-xs ${mutedClass}`}>Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${textClass}`}>{stats.pending}</p>
                <p className={`text-xs ${mutedClass}`}>En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Package className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${textClass}`}>{stats.processing}</p>
                <p className={`text-xs ${mutedClass}`}>En préparation</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <Truck className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${textClass}`}>{stats.shipped}</p>
                <p className={`text-xs ${mutedClass}`}>Expédiées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${textClass}`}>{stats.delivered}</p>
                <p className={`text-xs ${mutedClass}`}>Livrées</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className={cardClass}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Rechercher par n° commande, email ou nom..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-zinc-800 border-zinc-700'}`}
                data-testid="orders-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-full md:w-[200px] ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-zinc-800 border-zinc-700'}`}>
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmée</SelectItem>
                <SelectItem value="processing">En préparation</SelectItem>
                <SelectItem value="shipped">Expédiée</SelectItem>
                <SelectItem value="delivered">Livrée</SelectItem>
                <SelectItem value="cancelled">Annulée</SelectItem>
                <SelectItem value="refunded">Remboursée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className={cardClass}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${textClass}`}>
            <ShoppingCart className="w-5 h-5 text-primary" />
            Liste des commandes ({filteredOrders.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className={`w-12 h-12 mx-auto mb-4 ${mutedClass}`} />
              <p className={textClass}>Aucune commande trouvée</p>
              <p className={`text-sm ${mutedClass}`}>
                {searchQuery || statusFilter !== 'all' 
                  ? 'Essayez de modifier vos filtres'
                  : 'Les commandes apparaîtront ici'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isLight ? 'border-gray-200' : 'border-zinc-700'}>
                    <TableHead className={mutedClass}>N° Commande</TableHead>
                    <TableHead className={mutedClass}>Client</TableHead>
                    <TableHead className={mutedClass}>Date</TableHead>
                    <TableHead className={mutedClass}>Statut</TableHead>
                    <TableHead className={mutedClass}>Articles</TableHead>
                    <TableHead className={`text-right ${mutedClass}`}>Total</TableHead>
                    <TableHead className={`text-right ${mutedClass}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                    const StatusIcon = status.icon;
                    
                    return (
                      <TableRow 
                        key={order._id || order.order_number}
                        className={`cursor-pointer ${isLight ? 'hover:bg-gray-50 border-gray-200' : 'hover:bg-zinc-800/50 border-zinc-700'}`}
                        onClick={() => navigate(`/admin/orders/${order.order_number}`)}
                        data-testid={`order-row-${order.order_number}`}
                      >
                        <TableCell className={`font-mono font-medium ${textClass}`}>
                          #{order.order_number}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className={textClass}>{order.customer?.name || 'Client'}</p>
                            <p className={`text-xs ${mutedClass}`}>{order.customer?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className={mutedClass}>
                          {formatDate(order.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className={textClass}>
                          {order.items?.length || 0} article(s)
                        </TableCell>
                        <TableCell className={`text-right font-medium ${textClass}`}>
                          {formatPrice(order.total_amount, order.currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/orders/${order.order_number}`);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminOrdersPage;
